import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faChevronDown, faCircleCheck } from '@fortawesome/free-solid-svg-icons'

/**
 * A "needs attention" tile on the fleet overview.
 *
 * Always a button, and always looks like one: every tile in this row opens the
 * list behind its count, so nothing in the row is a plain stat that happens to
 * share the styling. Counts that are zero are not rendered as tiles at all -
 * the page shows them as small all-clear badges instead (AllClearBadge), so
 * clickable and non-clickable things never sit side by side.
 */

export type ActionTone = 'amber' | 'red' | 'gray'

const TONES: Record<ActionTone, { icon: string; accent: string; ring: string }> = {
  amber: { icon: 'bg-amber-50 text-amber-600', accent: 'border-l-amber-500', ring: 'ring-amber-500' },
  red: { icon: 'bg-red-50 text-red-600', accent: 'border-l-red-500', ring: 'ring-red-500' },
  gray: { icon: 'bg-gray-100 text-gray-500', accent: 'border-l-slate-400', ring: 'ring-slate-400' },
}

export default function ActionCard({
  title,
  value,
  subtitle,
  icon,
  tone,
  active,
  onClick,
  controls,
}: {
  title: string
  /** The count, or '…' while it loads. */
  value: number | string
  subtitle?: string
  icon: IconDefinition
  tone: ActionTone
  active: boolean
  onClick: () => void
  /** id of the panel this tile opens, for aria-controls. */
  controls: string
}) {
  const tones = TONES[tone]

  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={active}
      aria-controls={controls}
      className={`group flex w-full flex-col gap-3 rounded-lg border border-gray-200 border-l-4 ${tones.accent} bg-white p-4 text-left shadow-sm transition-all hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
        active ? `ring-2 ${tones.ring}` : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 rounded-full p-2.5 ${tones.icon}`}>
          <FontAwesomeIcon icon={icon} className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-2xl font-bold leading-tight text-gray-900">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">{subtitle}</p>}
        </div>
      </div>
      <span className="flex items-center gap-1.5 text-xs font-medium text-blue-600 group-hover:text-blue-700">
        {active ? 'Hide list' : 'View list'}
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`h-2.5 w-2.5 transition-transform ${active ? 'rotate-180' : ''}`}
        />
      </span>
    </button>
  )
}

/** A zero count, shown as a calm all-clear rather than a tile. Not clickable. */
export function AllClearBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-800">
      <FontAwesomeIcon icon={faCircleCheck} className="h-3.5 w-3.5 text-green-600" />
      {label}
    </span>
  )
}
